// Activity Tracker Service
// Tracks user online status in real-time
import { supabase as _sbClient } from '../config/supabase.js';

const getSupabaseClient = () => _sbClient;

class ActivityTracker {
    constructor() {
        this.updateInterval = null;
        this.isTracking = false;
    }

    // Start tracking user activity
    async startTracking(username) {
        if (this.isTracking) return;

        this.isTracking = true;
        console.log(`🟢 Started activity tracking for ${username}`);

        // Update immediately
        await this.updateActivity(username);

        // Update every 30 seconds
        this.updateInterval = setInterval(async () => {
            await this.updateActivity(username);
        }, 30000); // 30 seconds

        // Update on page activity (mouse move, click, keypress)
        this.setupActivityListeners(username);
    }

    // Stop tracking
    stopTracking(username) {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
        this.isTracking = false;
        console.log(`🔴 Stopped activity tracking for ${username}`);

        // Mark as offline
        this.markOffline(username);
    }

    // Update user's last active timestamp
    async updateActivity(username) {
        try {
            const supabase = getSupabaseClient();
            const now = new Date().toISOString();

            // Update or insert session - simplified to match actual schema
            const { error } = await supabase
                .from('sessions')
                .upsert({
                    session_id: username,
                    username: username,
                    role: 'agent', // Default role, will be overwritten by actual login
                    created_at: now
                }, { onConflict: 'session_id' });

            if (error) {
                // Silently fail - activity tracking is not critical
                console.debug('Activity tracking update skipped:', error.code);
            }
        } catch (error) {
            // Silently fail - activity tracking is not critical
            console.debug('Activity tracking error:', error.message);
        }
    }

    // Mark user as offline
    async markOffline(username) {
        try {
            const supabase = getSupabaseClient();
            await supabase
                .from('sessions')
                .delete()
                .eq('session_id', username);
        } catch (error) {
            console.error('Error marking offline:', error);
        }
    }

    // Get all active users (active in last 5 minutes)
    async getActiveUsers() {
        try {
            const supabase = getSupabaseClient();
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();

            const { data, error } = await supabase
                .from('sessions')
                .select('username')
                .gte('created_at', fiveMinutesAgo);

            if (error) {
                console.error('Error getting active users:', error);
                return [];
            }

            // Return usernames directly — sessions table stores username column
            return (data || []).map(s => s.username).filter(Boolean);
        } catch (error) {
            console.error('Error in getActiveUsers:', error);
            return [];
        }
    }

    // Setup activity listeners to update on user interaction
    setupActivityListeners(username) {
        let lastUpdate = Date.now();

        const updateIfNeeded = async () => {
            const now = Date.now();
            // Only update if more than 25 seconds since last update
            if (now - lastUpdate > 25000) {
                lastUpdate = now;
                await this.updateActivity(username);
            }
        };

        // Listen for user activity
        document.addEventListener('mousemove', updateIfNeeded, { passive: true });
        document.addEventListener('click', updateIfNeeded, { passive: true });
        document.addEventListener('keypress', updateIfNeeded, { passive: true });
    }
}

export const activityTracker = new ActivityTracker();
